/**
 * Combat feedback state for the post stack (pure, allocation-free; tested). Gameplay calls `setFeedback(damage,
 * boost)` every frame with 0..1 values (the damage pulse decays on the gameplay side); this module clamps them,
 * fires a short chromatic aberration kick when damage rises (a hit reaction only, ≤ 0.3 s: style guide §3.8)
 * and eases the boost grade in and out.
 */
import type { PostFile } from './data'

export type FeedbackConfig = PostFile['feedback']

export interface FeedbackState {
  /** Last clamped inputs. */
  damageIn: number
  boostIn: number
  /** Damage input seen by the previous step (rise detection). */
  prevDamage: number
  /** Seconds left on the chromatic aberration kick. */
  caLeft: number
  /** Outputs, 0..1: damage edge, chromatic aberration kick, boost grade. */
  edge: number
  ca: number
  boost: number
}

export function createFeedbackState(): FeedbackState {
  return { damageIn: 0, boostIn: 0, prevDamage: 0, caLeft: 0, edge: 0, ca: 0, boost: 0 }
}

/** Clamps to 0..1; NaN and non-finite values count as 0 (a bad caller must not flash the screen). */
export function clampUnit(v: number): number {
  if (!(v > 0)) return 0
  return v < 1 ? v : 1
}

export function setFeedbackInput(s: FeedbackState, damage: number, boost: number): void {
  s.damageIn = clampUnit(damage)
  s.boostIn = clampUnit(boost)
}

/** Longest frame step the feedback integrates (a stalled tab must not skip the kick in one jump). */
export const MAX_FEEDBACK_DT = 0.1

/** Advances the feedback by `dt` seconds and updates `edge`, `ca` and `boost`. */
export function stepFeedback(s: FeedbackState, cfg: FeedbackConfig, dt: number): void {
  const step = dt > 0 ? (dt < MAX_FEEDBACK_DT ? dt : MAX_FEEDBACK_DT) : 0
  if (s.damageIn - s.prevDamage >= cfg.caTrigger) s.caLeft = cfg.caDuration
  s.prevDamage = s.damageIn
  s.caLeft = s.caLeft > step ? s.caLeft - step : 0
  // Ease-out kick: full at the hit, zero after caDuration.
  const t = s.caLeft / cfg.caDuration
  s.ca = t * t
  s.edge = s.damageIn
  const k = 1 - Math.exp(-cfg.boostResponse * step)
  s.boost += (s.boostIn - s.boost) * k
  if (s.boost < 1e-4) s.boost = s.boostIn > 0 ? s.boost : 0
}
